// ─────────────────────────────────────────────────────────────────────────────
// The app's ONE chat engine — a module-scope singleton over the framework's
// matrix-lite client, the same shape of thing the realtime event hub is: minted
// once (from App, after auth is ready), alive for the whole session, consumed
// anywhere through subscribe/getters. The Messages screen renders it; the nav
// badges read only its unread total.
//
// Session contract (see ../../parsina.bff.web typeDefs/chat.js): the BFF mints a
// short-lived Matrix credential via the `chatSession` query; this module owns
// re-minting. `expiresAt` is epoch-ms-as-a-string, so it is Number()ed before
// arithmetic. A fresh session is requested 60s before expiry and pushed into
// the running client with `updateToken` — the sync loop never sees a dead
// token in the happy path. When the server 401s anyway (clock skew, revocation)
// matrix-lite pauses and calls `onAuthError`; the same re-mint path resumes it.
//
// Two things make that re-mint safe, and both were learned in the patient app:
//
//   * **One re-mint at a time.** `onAuthError` can fire in a burst (every
//     in-flight request 401s at once) and the scheduled timer can land in the
//     middle of it. Without a dedupe those mints race, and the LAST one to
//     resolve wins `updateToken` — which may well be the OLDEST, earliest-
//     expiring token, so the session dies again minutes later. `remintPromise`
//     is set synchronously before the first await, so the second caller joins
//     the first instead of starting a rival.
//   * **The credential is never held.** `session` keeps everything about the
//     session EXCEPT `accessToken` (see `publicSession`); the token goes
//     straight into the client and is not readable back out of this module.
//     Nothing is persisted either — no storage, no cookie, no URL.
//
// Visibility discipline mirrors useRealtimeEvents: a hidden tab does not hold a
// long-poll open — stop() on hidden, start() on visible. That is tracked with
// an explicit `paused` flag rather than leaning on matrix-lite's internal
// `started` guard: a tab that was never hidden must not be "resumed", and this
// module should not need to know how the client de-duplicates start().
// matrix-lite keeps its room state across a stop/start, so resuming is cheap.
//
// Everything impure is injectable through configureChatRuntime so the whole
// machine runs in a Node test with a scripted bffQuery and a fake client.
// ─────────────────────────────────────────────────────────────────────────────

import { useSyncExternalStore } from 'react';
import { bffQuery as appBffQuery } from '@parsina/framework/clinical/src/lib/bffClient';
import { createMatrixLite } from '@parsina/framework/ui/lib/matrix.js';

export const CHAT_SESSION_QUERY = `
  query ChatSession {
    chatSession {
      homeserverUrl
      userId
      accessToken
      deviceId
      expiresAt
      canSend
      readOnlyReason
    }
  }
`;

// Re-mint this long before the token dies, and never sooner than the floor —
// a clock that disagrees with the server must not spin a re-mint loop.
const REMINT_LEEWAY_MS = 60_000;
const REMINT_FLOOR_MS = 5_000;
// A failed re-mint retries on a fixed cadence; the session is still valid for
// the leeway window, so there is no reason to hammer.
const REMINT_RETRY_MS = 30_000;

const defaultDeps = () => ({ bffQuery: appBffQuery, createClient: createMatrixLite });

let deps = defaultDeps();
let client = null;
let session = null;        // the last ChatSession payload MINUS its credential
let startPromise = null;   // in-flight or resolved ensureChatStarted()
let remintPromise = null;  // in-flight remint() — dedupes the timer vs. a 401 burst
let sessionError = null;   // why the last start failed (null once started)
let remintTimer = null;
let unsubscribeClient = null;
let visibilityBound = false;
let paused = false;        // stopped because the tab went to the background
// Monotonic change counter. `getRooms()` / `getTimeline()` build a fresh array on
// every call, so they cannot BE a useSyncExternalStore snapshot — the store would
// hand React a new identity each render and loop forever. This integer is the
// snapshot; consumers read it and then derive the arrays through useMemo.
let revision = 0;
const listeners = new Set();

const emit = () => {
  revision += 1;
  for (const cb of [...listeners]) {
    try { cb(); } catch { /* one bad subscriber must not take the runtime down */ }
  }
};

/** Test seam: swap the impure edges. Call before ensureChatStarted(). */
export function configureChatRuntime(overrides = {}) {
  deps = { ...defaultDeps(), ...overrides };
}

/** Test seam: back to a cold, unconfigured runtime. */
export function resetChatRuntime() {
  clearTimeout(remintTimer);
  remintTimer = null;
  if (unsubscribeClient) unsubscribeClient();
  unsubscribeClient = null;
  try { client?.stop(); } catch { /* already stopped */ }
  client = null;
  session = null;
  startPromise = null;
  remintPromise = null;
  sessionError = null;
  paused = false;
  if (visibilityBound && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibility);
  }
  visibilityBound = false;
  listeners.clear();
  deps = defaultDeps();
}

const mintSession = async () => {
  const data = await deps.bffQuery(CHAT_SESSION_QUERY);
  const next = data?.chatSession;
  if (!next || !next.accessToken) throw new Error('chatSession: empty response');
  return next;
};

/**
 * Everything about a session except the credential. What the app needs from a
 * session is `canSend` / `readOnlyReason` / `expiresAt`; the access token is
 * needed exactly twice — at `createClient` and at `updateToken` — and both are
 * in this file. Keeping it in module scope afterwards only widens the reach of
 * anything that can read this module.
 */
const publicSession = (s) => ({
  homeserverUrl: s.homeserverUrl,
  userId: s.userId,
  deviceId: s.deviceId,
  expiresAt: s.expiresAt,
  canSend: s.canSend !== false,
  readOnlyReason: s.readOnlyReason || null,
});

function scheduleRemint() {
  clearTimeout(remintTimer);
  if (!session?.expiresAt) return;
  // Epoch-ms-as-a-string on the wire — Number() it, never Date-parse it.
  const delay = Math.max(Number(session.expiresAt) - Date.now() - REMINT_LEEWAY_MS, REMINT_FLOOR_MS);
  remintTimer = setTimeout(remint, delay);
}

function remint() {
  if (!client) return Promise.resolve();
  // Already minting — join it. The scheduled refresh and a 401 burst reach
  // here on the same tick often enough that letting both run would routinely
  // hand `updateToken` the older of the two tokens.
  if (remintPromise) return remintPromise;

  // The async body runs synchronously up to its first await, so `remintPromise`
  // is set before any caller can observe the gap.
  const run = async () => {
    const next = await mintSession();
    session = publicSession(next);
    // updateToken both replaces the credential and, after a 401 pause, resumes
    // the sync loop — one path serves the scheduled refresh and onAuthError.
    client.updateToken(next.accessToken);
    scheduleRemint();
    emit(); // canSend / readOnlyReason may have changed with the new session
  };

  remintPromise = run().then(
    () => { remintPromise = null; },
    () => {
      remintPromise = null;
      clearTimeout(remintTimer);
      remintTimer = setTimeout(remint, REMINT_RETRY_MS);
    },
  );
  return remintPromise;
}

function onVisibility() {
  if (!client || typeof document === 'undefined') return;
  // Mirror useRealtimeEvents: don't hold a long-poll open for a hidden tab;
  // resume the moment the doctor looks back at it.
  if (document.visibilityState === 'hidden') {
    client.stop();
    paused = true;
    return;
  }
  // Only a tab that WE stopped is resumed. `start()` on every visibility change
  // leaned on matrix-lite's internal `started` guard to be a no-op — a
  // downstream implementation detail this module has no business assuming.
  if (!paused) return;
  paused = false;
  client.start();
}

/**
 * Idempotent start. The first caller mints the session, builds the client and
 * starts syncing; everyone else gets the same promise. A failed start clears
 * the promise so the next call retries.
 */
export function ensureChatStarted() {
  if (startPromise) return startPromise;
  startPromise = (async () => {
    sessionError = null;
    const minted = await mintSession();
    session = publicSession(minted);
    client = deps.createClient({
      baseUrl: minted.homeserverUrl,
      accessToken: minted.accessToken,
      userId: minted.userId,
      onAuthError: () => { remint(); },
    });
    unsubscribeClient = client.onChange(emit);
    client.start();
    scheduleRemint();
    if (!visibilityBound && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
      visibilityBound = true;
    }
    emit();
    return client;
  })().catch((err) => {
    sessionError = err;
    startPromise = null; // the next ensureChatStarted() is a real retry
    emit();
    throw err;
  });
  return startPromise;
}

export const getChatClient = () => client;
/**
 * The current session WITHOUT its credential — never the raw payload. Callers
 * that want the token do not exist; the two places that need it are in this
 * file.
 */
export const getChatSession = () => session;
export const getChatSessionError = () => sessionError;
export const getTotalUnread = () => (client ? client.totalUnread() : 0);
/** The store snapshot (see `revision`): a stable integer, bumped on every change. */
export const getChatRevision = () => revision;
// Reads are never gated; an absent session (still starting) must not flash the
// read-only notice, so only an explicit false hides the composer.
export const getCanSend = () => session?.canSend !== false;
export const getReadOnlyReason = () => (session?.canSend === false ? session?.readOnlyReason || null : null);

/** Notified on every matrix change AND every session change. Returns unsubscribe. */
export function subscribeChat(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Live total-unread for the nav badges — anywhere in the tree, no props. */
export function useChatUnread() {
  return useSyncExternalStore(subscribeChat, getTotalUnread, () => 0);
}
